import type { TaxIdentityResource } from '@repo/api-client';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { useMemo } from 'react';

import { fieldSurface } from '../auth/AuthFrame';
import { optionLabelForTaxIdentity } from './registrations';

/** The registration picker shared by the create and edit dialogs. */
export function RegistrationSelect({
  id,
  value,
  onChange,
  identities,
  errorId,
}: {
  id: string;
  /** `''` is nothing chosen; the schema decides whether that is allowed. */
  value: string;
  onChange: (value: string) => void;
  identities: readonly TaxIdentityResource[];
  /** The `FormMessage` id while it has something to say, otherwise `undefined`. */
  errorId: string | undefined;
}) {
  // Base UI resolves the trigger's label from `items`, not from the options.
  const items = useMemo(
    () =>
      identities.map((identity) => ({
        label: optionLabelForTaxIdentity(identity),
        value: identity.id,
      })),
    [identities],
  );

  return (
    // `null` rather than `''` when nothing is chosen, so the placeholder shows.
    <Select
      value={value === '' ? null : value}
      items={items}
      onValueChange={(next) => onChange(next ?? '')}
    >
      <SelectTrigger
        id={id}
        aria-label="GST registration"
        aria-invalid={errorId !== undefined}
        aria-describedby={errorId}
        className={`${fieldSurface} h-11 w-full`}
      >
        <SelectValue placeholder="Choose a registration" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
